import React, { useState, useEffect } from 'react';
import { uniq, flatten } from 'ramda';
import { Redirect } from 'react-router';
import { FileText } from 'react-feather';
import PropTypes from 'prop-types';

import { FormattedMessage } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import { GET_BOOKING_ITEMS } from 'store/queries';
import { bookingStatus } from 'types/booking';
import { bookingShape, userShape } from 'constants/propTypes';
import { RECEIPT_DATA_ADDITIONAL_DETAILS_KEY } from 'constants/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { Row, Column, ResponsiveColumn } from 'components/shared/Layout';
import PaymentsSummary from 'components/shared/PaymentsSummary';
import CustomerDetails from 'components/shared/CustomerDetails';
import Spinner from 'admin-components/components/Spinner';
import { RowHeader, StyledRow, StyledActionButton } from './Payments.styles';
import Panel from './Panel';
import Ticket from './Summary/Ticket';
import Product from './Summary/Product';
import BookingFees from './BookingFees';
import OtherFees from './OtherFees';
import { getPurchased, hasTrainSegment, getTicketsTotal } from './Payments.utils';
import PaymentsOutstanding from './PaymentsOutstanding/PaymentsOutstanding';
import ExtrasHotel from './Extras/ExtrasHotel';
import ExtrasInsurance from './Extras/ExtrasInsurance';
import SendQuoteConfirmationEmailButton from './SendQuoteConfirmationEmailButton';
import { isEurostarOrganization, isTravelAgent, isUserThalysAgent, userCanOfferQuotes } from 'utils/user';
import { getQuoteConfirmationStatus } from 'pages/QuoteConfirmation/utils';
import { isEurostarOffer } from 'utils/isEurostarOffer';

const Payments = ({ currency, booking, user }) => {
  const [shouldRedirectToReceiptPage, redirectToReceiptPage] = useState(false);
  const [redirectToQuotePage, setRedirectToQuotePage] = useState(false);
  const [
    getBookingItems,
    { data: bookingItemsData, error: bookingItemsError, loading: bookingItemsLoading, called: getBookingItemsCalled }
  ] = useLazyQuery(GET_BOOKING_ITEMS);
  const bookingItems = bookingItemsData?.getBookingItems;

  const {
    reference,
    offlineFees = [],
    hotelExchangeFees = [],
    hotelCancellationFees = [],
    hotelMarginCancellationFees = [],
    insuranceCancellationFees = [],
    flowForwardFees = [],
    impulseUpgradeFees = [],
    travelPassProductsCancellationFees = [],
    bidUpgradeFees = [],
    insurances = [],
    payments,
    outbound,
    inbound,
    totalPriceToBePaid,
    status,
    displayStatus,
    outboundExtras,
    hotels,
    customer,
    vouchers,
    isLoyalty,
    additionalDetails
  } = booking;

  const {
    outboundTickets,
    inboundTickets,
    outboundExtraProducts,
    inboundExtraProducts,
    bookingFees,
    productFees,
    otherOutboundFees,
    otherInboundFees,
    extrasOnlyBookingProducts
  } = getPurchased({
    fees: flatten([
      offlineFees,
      hotelExchangeFees,
      hotelCancellationFees,
      hotelMarginCancellationFees,
      bidUpgradeFees,
      insuranceCancellationFees,
      flowForwardFees,
      impulseUpgradeFees,
      travelPassProductsCancellationFees
    ]),
    outbound,
    inbound,
    outboundExtras
  });

  const outboundTotal = getTicketsTotal(outboundTickets);
  const inboundTotal = getTicketsTotal(inboundTickets);
  const ticketsTotal = outboundTotal + inboundTotal;

  const nonEurostarTariffCodes = uniq(
    [...outboundTickets, ...inboundTickets].filter((t) => t.serviceType !== serviceTypeCodes.EUROSTAR).map((t) => t.code)
  );

  useEffect(() => {
    if (!nonEurostarTariffCodes.length || getBookingItemsCalled) {
      return;
    }
    getBookingItems({
      variables: {
        bookingReference: reference,
        tariffCodes: nonEurostarTariffCodes
      }
    });
  }, [getBookingItems, nonEurostarTariffCodes, reference, getBookingItemsCalled]);

  const feeRefundableStatuses = [bookingStatus.CONFIRMED, bookingStatus.CANCELLED, bookingStatus.EXPIRED_ON_HOLD, bookingStatus.REFUNDED];
  const isPaymentsOutstandingVisible = [bookingStatus.ON_HOLD, bookingStatus.TEMPORARILY_ON_HOLD].includes(status);
  const isEurostarAgent = isEurostarOrganization(user);
  const feeStatus = displayStatus || status;
  const isFeesStatusAllowed = feeRefundableStatuses.includes(feeStatus);
  let feesDisabledTooltipMessageId = '';

  if (!isEurostarAgent) {
    feesDisabledTooltipMessageId = 'paymentsTab.otherFees.nonEurostarAgent';
  } else if (!isFeesStatusAllowed) {
    feesDisabledTooltipMessageId = 'paymentsTab.otherFees.nonConfirmedBooking';
  }

  const areFeesDisabled = Boolean(feesDisabledTooltipMessageId);
  const isPaymentsSummaryVisible = payments.length > 0;

  const paymentsWithReceipts = payments.map((payment) => {
    const receiptDetails = additionalDetails.find(
      (detail) => detail.itemRef === payment.id && detail.key === RECEIPT_DATA_ADDITIONAL_DETAILS_KEY
    );
    if (!receiptDetails) {
      return payment;
    }

    return {
      ...payment,
      receiptData: JSON.parse(receiptDetails.value)
    };
  });

  const rawExtras = hasTrainSegment(booking) ? outboundExtraProducts : extrasOnlyBookingProducts;

  const isPriceMoreThanZero = ({ price }) => price > 0;

  const outboundExtrasToDisplay = rawExtras.filter(isPriceMoreThanZero);

  const inboundExtrasToDisplay = inboundExtraProducts.filter(isPriceMoreThanZero);

  const hotelsToDisplay = hotels && hotels.filter((h) => !h.cancelled);
  const insurancesToDisplay = insurances && insurances.filter((i) => !i.cancelled);

  const shouldDisplayHotels = hotelsToDisplay && hotelsToDisplay.length > 0;
  const shouldDisplayInsurances = insurancesToDisplay && insurancesToDisplay.length > 0;
  const isStationUser = user.isStation;
  const isLondonRoute = isEurostarOffer([...(outbound ?? []), ...(inbound ?? [])]);
  const displayQuoteButton = isLondonRoute && userCanOfferQuotes(user);
  const quoteButtonDisabled = booking.status !== bookingStatus.ON_HOLD && booking.status !== bookingStatus.TEMPORARILY_ON_HOLD;
  const displayQuoteSummary =
    displayQuoteButton || (isLondonRoute && isTravelAgent(user) && !isUserThalysAgent(user) && !quoteButtonDisabled);

  const quotationStatus = getQuoteConfirmationStatus(status, payments) ?? 'buttonTitle';

  if (bookingItemsLoading || (nonEurostarTariffCodes.length && !bookingItemsData && !bookingItemsError)) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (shouldRedirectToReceiptPage) {
    return <Redirect push to={`/manage/bookings/${reference}/receipt`} />;
  }

  if (redirectToQuotePage) {
    return <Redirect push to={`/manage/bookings/${reference}/quote-confirmation`} />;
  }

  return (
    <Row data-cy="payments-tab">
      <Row span={14} start={2} alignItems="center">
        <ResponsiveColumn smallScreens={14} bigScreens={4}>
          <h1>
            <FormattedMessage id="paymentsTab.title" />
          </h1>
        </ResponsiveColumn>
        <ResponsiveColumn smallScreens={12} bigScreens={11} justifyContent="flex-end">
          {displayQuoteButton && (
            <SendQuoteConfirmationEmailButton booking={booking} quotationStatus={quotationStatus} disabled={quoteButtonDisabled} />
          )}
          {displayQuoteSummary && (
            <StyledActionButton
              onClick={() => setRedirectToQuotePage(true)}
              disabled={quoteButtonDisabled}
              data-cy="view-quote-confirmation"
            >
              <FileText />
              <FormattedMessage id={`viewQuoteConfirmation.${quotationStatus}`} />
            </StyledActionButton>
          )}
        </ResponsiveColumn>
        <ResponsiveColumn smallScreens={4} bigScreens={1} justifyContent="flex-end">
          <StyledActionButton onClick={() => redirectToReceiptPage(true)} data-cy="view-receipt">
            <FileText />
            <FormattedMessage id="paymentsTab.viewReceipt" />
          </StyledActionButton>
        </ResponsiveColumn>
      </Row>

      {isPaymentsOutstandingVisible && (
        <Row span={14} start={2}>
          <RowHeader>
            <FormattedMessage id="paymentsOutstanding.header" />
          </RowHeader>
          <PaymentsOutstanding booking={booking} />
        </Row>
      )}

      <Row span={14} start={2}>
        <RowHeader>
          <FormattedMessage id="payment.customerDetails" />
        </RowHeader>
        <CustomerDetails customer={customer} />
      </Row>

      {isPaymentsSummaryVisible && (
        <Row span={14} start={2}>
          <RowHeader>
            <FormattedMessage id="payment.paymentsSummary" />
          </RowHeader>
          <PaymentsSummary payments={paymentsWithReceipts} bookingReference={reference} isStationUser={isStationUser} customer={customer} />
        </Row>
      )}

      <Row span={14} start={2}>
        <RowHeader>
          <FormattedMessage id="paymentsTab.bookingDetails" />
        </RowHeader>
        <Panel title="bookingSummary.tickets" subtitle={<FormattedCurrency amount={ticketsTotal} currency={currency} />}>
          <>
            <RowHeader>
              <FormattedMessage id="journeySummary.outbound" /> - <FormattedCurrency amount={outboundTotal} currency={currency} />
            </RowHeader>
            {outboundTickets.length > 0 ? (
              outboundTickets.map((ticket) => (
                <Ticket
                  key={`outbound${ticket.code}`}
                  ticket={ticket}
                  costPrice={bookingItems?.find((i) => i.id === ticket.uuid)?.costPrice}
                  vouchers={vouchers}
                  isLoyalty={isLoyalty}
                />
              ))
            ) : (
              <FormattedMessage id="paymentsTab.none" />
            )}
          </>

          <>
            <RowHeader>
              <FormattedMessage id="journeySummary.inbound" /> - <FormattedCurrency amount={inboundTotal} currency={currency} />
            </RowHeader>
            {inboundTickets.length > 0 ? (
              inboundTickets.map((ticket) => (
                <Ticket
                  key={`inbound${ticket.code}`}
                  ticket={ticket}
                  costPrice={bookingItems?.find((i) => i.id === ticket.uuid)?.costPrice}
                  vouchers={vouchers}
                  isLoyalty={isLoyalty}
                />
              ))
            ) : (
              <FormattedMessage id="paymentsTab.none" />
            )}
          </>
        </Panel>
      </Row>

      <Row span={14} start={2}>
        <RowHeader>
          <FormattedMessage id="paymentsTab.extrasAndFees" />
        </RowHeader>
        <Panel title="paymentsTab.extras">
          <>
            <RowHeader>
              <FormattedMessage id="journeySummary.outbound" />
            </RowHeader>
            {outboundExtrasToDisplay.length > 0 ? (
              outboundExtrasToDisplay.map((product) => <Product key={`outbound${product.code}`} product={product} />)
            ) : (
              <FormattedMessage id="paymentsTab.none" />
            )}
          </>

          <>
            <RowHeader>
              <FormattedMessage id="journeySummary.inbound" />
            </RowHeader>
            {inboundExtrasToDisplay.length > 0 ? (
              inboundExtrasToDisplay.map((product) => <Product key={`inbound${product.code}`} product={product} />)
            ) : (
              <FormattedMessage id="paymentsTab.none" />
            )}
          </>
        </Panel>

        <StyledRow>
          <RowHeader>
            <FormattedMessage id="extras.hotel.title" />
          </RowHeader>
          {shouldDisplayHotels ? (
            hotelsToDisplay.map((hotel) => <ExtrasHotel key={hotel.itemRef} hotel={hotel} />)
          ) : (
            <FormattedMessage id="paymentsTab.none" />
          )}
        </StyledRow>

        <StyledRow>
          <RowHeader>
            <FormattedMessage id="extras.insurance.title" />
          </RowHeader>
          {shouldDisplayInsurances ? (
            insurances.map((insurance) => <ExtrasInsurance key={insurance.itemRef} insurance={insurance} />)
          ) : (
            <FormattedMessage id="paymentsTab.none" />
          )}
        </StyledRow>
      </Row>

      <Row span={14} start={2}>
        <BookingFees
          toBeRefunded={{ currency, amount: totalPriceToBePaid }}
          bookingReference={reference}
          bookingFees={bookingFees.concat(productFees)}
          bookingStatus={status}
          disabled={areFeesDisabled}
          disabledTooltipMessageId={feesDisabledTooltipMessageId}
        />
      </Row>

      <Row span={14} start={2}>
        <OtherFees
          toBeRefunded={{ currency, amount: totalPriceToBePaid }}
          bookingReference={reference}
          otherOutboundFees={otherOutboundFees}
          otherInboundFees={otherInboundFees}
          disabled={areFeesDisabled}
          disabledTooltipMessageId={feesDisabledTooltipMessageId}
        />
      </Row>
    </Row>
  );
};

Payments.propTypes = {
  currency: PropTypes.string.isRequired,
  booking: bookingShape.isRequired,
  user: userShape.isRequired
};

export default Payments;
